import { useEffect, useState } from "react";

import { useAuth } from "@/features/auth/AuthProvider";
import { supabase } from "@/lib/supabaseClient";

/**
 * SPEC-22 — admin detection.
 *
 * Reads the current user's `profiles.role` row (RLS allows a user to
 * read their own profile, per SPEC-21). Returns `isAdmin` and a
 * `loading` flag so callers can gate UI on a stable answer instead of
 * flashing the non-admin state during the round-trip.
 *
 * Failures (missing profile row, RLS denial, network error) fall back
 * to `isAdmin: false` — this is a gating decision, not an audit log.
 */
export function useIsAdmin(): { isAdmin: boolean; loading: boolean } {
  const { user, loading: authLoading } = useAuth();
  const [isAdmin, setIsAdmin] = useState(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let cancelled = false;
    if (authLoading) return;
    if (!user) {
      setIsAdmin(false);
      setLoading(false);
      return;
    }
    // Finding E3 — reset to the non-admin baseline BEFORE the async query so a
    // user switch (admin signs out, non-admin signs in, same tab) cannot
    // briefly surface the previous user's isAdmin=true during the round-trip.
    setIsAdmin(false);
    setLoading(true);
    supabase
      .from("profiles")
      .select("role")
      .eq("id", user.id)
      .maybeSingle()
      .then(
        ({ data }) => {
          if (cancelled) return;
          setIsAdmin(data?.role === "admin");
          setLoading(false);
        },
        () => {
          // Failures fall back to non-admin (gating decision, not an audit log).
          // (The query builder is a PromiseLike, so use the onRejected arg.)
          if (cancelled) return;
          setIsAdmin(false);
          setLoading(false);
        },
      );
    return () => {
      cancelled = true;
    };
  }, [user, authLoading]);

  return { isAdmin, loading };
}
